import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { ArrowUpRightIcon } from 'lucide-react';
import type { MouseEvent, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatOrderId } from '../../../lib/order-helpers.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';

type Props = {
    transactionId: string;
    type: SrSalesOrderType;
};

function getTransactionUrl(type: SrSalesOrderType, id: string) {
    if (type === SrSalesOrderType.QUOTE) {
        return `/quotes/${encodeURIComponent(id)}`;
    }

    if (type === SrSalesOrderType.ORDER) {
        return `/orders/${encodeURIComponent(id)}`;
    }

    return ``;
}

function useOpenTransactionOnClick(type: SrSalesOrderType, transactionId: string) {
    const { openOrderQuoteDetails } = useDialogs();

    return (e: MouseEvent<HTMLAnchorElement>) => {
        if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            openOrderQuoteDetails(type, transactionId);
        }

        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
    };
}

export function QuoteOrderLink({ type, transactionId }: Props) {
    const appConfig = useAppConfig();
    const openTransaction = useOpenTransactionOnClick(type, transactionId);

    return (
        <Link
            className="text-blue-700 hover:underline text-sm"
            to={getTransactionUrl(type, transactionId)}
            onClick={openTransaction}
        >
            {formatOrderId(appConfig, transactionId)}
        </Link>
    );
}

export function QuoteOrderTitleLink({
    type,
    transactionId,
    children,
}: Props & { children: ReactNode }): ReactElement {
    const openTransaction = useOpenTransactionOnClick(type, transactionId);

    return (
        <Link
            className="group/link flex min-w-0 items-center gap-tight text-body-emphasis text-content"
            to={getTransactionUrl(type, transactionId)}
            onClick={openTransaction}
        >
            <span className="truncate group-hover/link:underline group-hover/row:underline">{children}</span>
            <ArrowUpRightIcon className="hidden size-3.5 shrink-0 text-neutral-300 group-hover/link:text-neutral-500 sm:block" />
        </Link>
    );
}

export function OrderLink(props: Omit<Props, 'type'>) {
    return <QuoteOrderLink type={SrSalesOrderType.ORDER} {...props} />;
}

export function QuoteLink(props: Omit<Props, 'type'>) {
    return <QuoteOrderLink type={SrSalesOrderType.QUOTE} {...props} />;
}
